<script lang="ts">
  // TrainStopScreen: the whole R160 car-front display — the Helvetica destination panel on
  // the left (`DestinationSign`) and the amber dot-matrix rollsign strip on the right, side
  // by side as they are on the real unit. Two devices, one component, because the split is
  // what makes the drawer short: everything in the left panel used to be stacked rows above
  // the strip. See the "split" section of `shell.ts`.
  //
  // In the strip, stops run left to right along a horizontal rail line with slanted tick
  // marks representing stops away. Station names and callouts are rotated to align with it.
  //
  // ⚠️ The rotated element is the BUTTON, and the column around it is inert. A label overhangs
  // its own column by design — up to 282px of diagonal out of 96px of column, and the next
  // stop's red bar reaches over the two columns behind it — so an upright, column-shaped hit
  // region is not the shape of the thing a rider aims at: tapping the frame that read
  // "34 St-Herald Sq" picked 47-50 Sts. Browsers hit-test through `transform`, so putting the
  // handler on the rotated node is the whole fix. It costs the rail, the tick and the
  // "3 Stops" counter their tappability, which is right — they name no stop.
  import type { Snippet } from 'svelte'
  import type { Stop } from '../../store/rider'
  import { useRiderStore } from '../../store/rider'
  import { useStopsStore } from '../../store/stops'
  import { storeSelect, storeState } from '../../lib/store.svelte'
  import { parentStopId } from '../Map/timetable'
  import { baseRouteId, compareRoutes, sameRouteFamily } from '../../ui/colors'
  import { api } from '../../services/api'
  import {
    LED_AMBER,
    LED_FONT,
    LED_GREEN,
    LED_RAIL,
    LED_RED,
    ledGlow,
  } from '../../ui/theme'
  import {
    rollsignNamePx,
    ROLLSIGN_COL_W,
    ROLLSIGN_LABEL_ANGLE,
    ROLLSIGN_LABEL_BLOCK_H,
    ROLLSIGN_LABEL_LEFT,
    ROLLSIGN_LABEL_RUN_PX,
    ROLLSIGN_LABEL_TOP,
    ROLLSIGN_RAIL_BOTTOM,
    ROLLSIGN_RAIL_H,
    ROLLSIGN_RAIL_SVG_H,
    rollsignRailPath,
    ROLLSIGN_STRIP_H,
    ROLLSIGN_TICK_LEN,
    ROLLSIGN_TICK_ROTATE,
    ROLLSIGN_TICK_X,
    ROLLSIGN_TRANSFER_PX,
    NEXT_ANGLE,
    NEXT_BOX_BORDER,
    NEXT_BOX_BOTTOM,
    NEXT_BOX_H,
    NEXT_BOX_INNER_W,
    NEXT_BOX_LEFT,
    NEXT_BOX_PAD_X,
    NEXT_BOX_W,
    NEXT_COL_W,
    NEXT_LABEL_GAP,
    NEXT_LABEL_PX,
    STRIP_PAD_X,
  } from './shell'
  import DestinationSign from './DestinationSign.svelte'

  interface Props {
    /** Stops still ahead, nearest first. `stops[0]` is the next stop. */
    stops: Stop[]
    destinationStopId: string | null
    /** Tapping a stop flags it as where the rider gets off. */
    onPick: (stopId: string) => void
    /** Optional override for whether train is arriving at next stop. */
    isArriving?: boolean
    /** The destination panel's contents — see `DestinationSign`. */
    routeId: string
    terminus: string | null
    notice: string | null
    noticeUrgent: boolean
    /**
     * Handed straight to the band's own `trailing` slot — the panel's ✕. Passed through
     * rather than rendered here because the band owns its right end, and this screen owns
     * only the band's place above the strip.
     */
    signTrailing?: Snippet
  }

  const {
    stops,
    destinationStopId,
    onPick,
    isArriving: propIsArriving,
    routeId,
    terminus,
    notice,
    noticeUrgent,
    signTrailing,
  }: Props = $props()

  const rider = storeState(useRiderStore)
  const stopsData = storeSelect(useStopsStore, (s) => s.stops)

  const currentRouteId = $derived(rider.value.trackedVehicle?.routeId ?? null)

  const isArriving = $derived.by(() => {
    if (propIsArriving !== undefined) return propIsArriving
    if (rider.value.trackedVehicle?.status === 'INCOMING_AT') return true
    if (nextStop?.arrivalTime) {
      const arrTime = nextStop.arrivalTime > 1e11 ? nextStop.arrivalTime : nextStop.arrivalTime * 1000
      const diff = arrTime - Date.now()
      if (diff <= 60000 && diff >= -30000) return true
    }
    return false
  })

  // Maps parent station IDs and member IDs to reachable transfer routes.
  const routesByStop = $derived.by(() => {
    const map = new Map<string, string[]>()
    for (const s of stopsData.value) {
      const transferRoutes = s.transferRoutes ?? s.routes
      map.set(s.id, transferRoutes)
      for (const m of s.memberIds ?? []) {
        map.set(m, transferRoutes)
      }
    }
    return map
  })

  // Maps parent station IDs and member IDs to accessibility status.
  const accessibleByStop = $derived.by(() => {
    const map = new Map<string, boolean>()
    for (const s of stopsData.value) {
      if (s.accessible) {
        map.set(s.id, true)
        if (s.name) {
          map.set(s.name.toLowerCase(), true)
          map.set(s.name.toLowerCase().replace(/[^a-z0-9]/g, ''), true)
        }
        for (const m of s.memberIds ?? []) {
          map.set(m, true)
        }
      }
    }
    return map
  })

  function isStationAccessible(stopId: string, stopName?: string, stopAccessible?: boolean): boolean {
    if (stopAccessible !== undefined) return stopAccessible
    const pid = parentStopId(stopId)
    if (accessibleByStop.get(pid) || accessibleByStop.get(stopId)) return true
    if (stopName) {
      const lower = stopName.toLowerCase()
      if (accessibleByStop.get(lower)) return true
      const cleaned = lower.replace(/[^a-z0-9]/g, '')
      if (accessibleByStop.get(cleaned)) return true
    }
    return false
  }

  let liveRoutes = $state<Record<string, string[]> | null>(null)

  // Ensures station routes and accessibility are available even if the rider opens straight into riding mode.
  $effect(() => {
    const hasAccessible = stopsData.value.some((s) => s.accessible !== undefined)
    if (stopsData.value.length === 0 || !hasAccessible) {
      api.getStopsWithRoutes().then((res) => {
        if (res?.stops) {
          useStopsStore.getState().setStops(res.stops)
        }
      }).catch(() => {})
    }

    const fetchLive = () => {
      api.getLiveStationRoutes().then((res) => {
        if (res?.stops && Object.keys(res.stops).length > 0) {
          liveRoutes = res.stops
        }
      }).catch(() => {})
    }

    fetchLive()
    const timer = setInterval(fetchLive, 30000)
    return () => clearInterval(timer)
  })

  /**
   * Returns list of transferrable train lines for a given station, excluding the current line family
   * and any lines not currently serving the station live.
   */
  function getStationTransferRoutes(stopId: string): string[] {
    const pid = parentStopId(stopId)
    const routes = routesByStop.get(pid) ?? []
    if (routes.length === 0) return []

    const curRoute = currentRouteId ? baseRouteId(currentRouteId) : ''
    const live = liveRoutes?.[pid]
    const running = live && live.length > 0 ? new Set(live.map((r) => baseRouteId(r))) : null

    const filtered = routes.filter((r) => {
      const base = baseRouteId(r)
      if (running && !running.has(base)) return false
      if (!curRoute) return true
      return !sameRouteFamily(r, curRoute)
    })

    // Deduplicate by base route id (e.g. 6X -> 6, 7X -> 7)
    const byBase = new Map<string, string>()
    for (const r of filtered) {
      const base = baseRouteId(r)
      if (!byBase.has(base) || r === base) {
        byBase.set(base, base)
      }
    }

    return [...byBase.values()].sort(compareRoutes)
  }

  const nextStop = $derived(stops[0] ?? null)
  const restStops = $derived(stops.slice(1))

  // VT323 is a `font-display: swap` webfont, so a name measured before it lands is measured
  // in Courier New — wider, and the label would be set smaller than it needs to be. Nothing
  // else re-renders a stop whose name hasn't changed, so that first guess would stand for the
  // whole ride; one bump of the epoch re-fits every label the moment the real face is ready.
  let fontEpoch = $state(0)
  $effect(() => {
    const fonts = typeof document !== 'undefined' ? document.fonts : undefined
    if (!fonts) return
    let alive = true
    fonts.load('20px VT323').then(() => { if (alive) fontEpoch += 1 }).catch(() => {})
    return () => { alive = false }
  })
  /** `rollsignNamePx`, re-created (and so re-read by the labels) when the LED face lands. */
  const fitName = $derived.by(() => {
    fontEpoch
    return (label: string, runPx?: number) => rollsignNamePx(label, runPx)
  })

  // The callout column stays narrower than the bar is long: the bar runs 164px right of its
  // corner, over the columns behind it. That is only safe because the bar and the labels are
  // the SAME diagonal — parallel lines that never converge. Column 2's label starts ~31px
  // perpendicular below the bar's lower edge and stays there, and it grows away from the bar,
  // down-right. On the old 52° labels it climbed steeper than the bar and cut through it.
  // `NEXT_COL_W` is what holds that clearance; it lives in `shell.ts` beside the bar it is
  // measured against, and beside the test that checks the two still agree.
  const COL_W = ROLLSIGN_COL_W
  const STRIP_H = ROLLSIGN_STRIP_H

  // The bar and the "Next Stop" label both pivot about the bar's bottom-left corner, so the
  // label rides the bar's own diagonal instead of being placed against it by eye: the
  // translate happens in the unrotated frame and the rotation carries it, which puts the
  // label exactly NEXT_BOX_H + NEXT_LABEL_GAP perpendicular from the bar and starting at the
  // same point along it. Geometry and the reference measurements are in `shell.ts`.
  const nextBarTransform = `rotate(-${NEXT_ANGLE}deg)`
  const nextLabelTransform =
    `rotate(-${NEXT_ANGLE}deg) translateY(-${NEXT_BOX_H + NEXT_LABEL_GAP}px)`
</script>

{#snippet stopColumn(
  stopId: string,
  name: string,
  stopsAway: number,
  width: number,
  isDest: boolean,
  isLastStop: boolean = false,
  accessible?: boolean,
  zIndex?: number,
)}
  {@const transfers = getStationTransferRoutes(stopId)}
  {@const isAda = isStationAccessible(stopId, name, accessible)}
  {@const label = `${name}${isAda ? ' ♿' : ''}${isDest ? ' 🏁' : ''}`}
  <!-- The column is a plain box: the TARGET is the rotated label below, not this. A label
       overhangs its own 96px column by design (it runs up to 282px on the diagonal), so an
       upright column-shaped hit region and the name a rider is actually aiming at are two
       different shapes — tapping the far end of "42 St-Bryant Pk" picked the stop after it. -->
  <div
    style="
      position: relative; flex: none; width: {width}px; height: 100%;
      font-family: {LED_FONT};
      z-index: {zIndex ?? 1};
    "
  >
    <div
      style="
        position: absolute; left: 0; right: 0;
        bottom: {ROLLSIGN_RAIL_BOTTOM}px; height: {ROLLSIGN_RAIL_H}px; background: {LED_RAIL};
      "
    ></div>
    <!-- The tick rides the labels' own diagonal, so it points up the line the name is set
         on. Authored upright and rotated, so the rotation is off VERTICAL. Its foot is a few
         px below the rail's top edge, which is what joins them. -->
    <div
      style="
        position: absolute; bottom: 52px; left: {ROLLSIGN_TICK_X}px;
        width: {ROLLSIGN_RAIL_H}px; height: {ROLLSIGN_TICK_LEN}px;
        background: {LED_RAIL};
        transform: rotate({ROLLSIGN_TICK_ROTATE}deg); transform-origin: bottom center;
      "
    ></div>
    <!-- The label IS the button, so the hit region lies on the same 35° the name does. Its
         height is pinned to ROLLSIGN_LABEL_BLOCK_H — the tallest a label gets — rather than
         left to the content, so a stop with no transfer row is no harder to hit than one
         with; that is also the height the 55px perpendicular separation between neighbouring
         labels is budgeted against, so the targets stack up without overlapping. -->
    <button
      onclick={() => onPick(stopId)}
      aria-label={isDest ? `Don't get off at ${name}` : `Get off at ${name}`}
      aria-pressed={isDest}
      style="
        position: absolute; top: {ROLLSIGN_LABEL_TOP}px; left: {ROLLSIGN_LABEL_LEFT}px;
        transform: rotate(-{ROLLSIGN_LABEL_ANGLE}deg); transform-origin: top left;
        white-space: nowrap; text-align: left;
        display: flex; flex-direction: column; align-items: flex-start;
        height: {ROLLSIGN_LABEL_BLOCK_H}px;
        background: none; border: none; padding: 0; cursor: pointer; font: inherit;
      "
    >
      <!-- max-width is the backstop for a name so long that even the 12px floor overruns
           ("Sutphin Blvd-Archer Av-JFK Airport" once it also carries ♿ and 🏁): it
           ellipsizes, which is a deliberate end to a name, where the strip's own clip is a
           name that just stops. The 35° face made that list much shorter than it was. -->
      <div
        style="
          font-size: {fitName(label)}px; line-height: 1.2; letter-spacing: 1px;
          max-width: {ROLLSIGN_LABEL_RUN_PX}px; overflow: hidden; text-overflow: ellipsis;
          {ledGlow(isDest ? LED_GREEN : LED_AMBER, 8, 0.55)}
        "
      ><span>{name}</span>{#if isAda}<span style="font-size: 0.625em; line-height: 1; vertical-align: middle; margin: 0 1px 0 2px;">♿</span>{/if}{#if isDest} 🏁{/if}</div>
      {#if transfers.length > 0}
        <div style="font-size: {ROLLSIGN_TRANSFER_PX}px; letter-spacing: 1px; {ledGlow(LED_GREEN, 6, 0.55)}; margin-top: 2px;">
          {transfers.join('·')}
        </div>
      {/if}
    </button>
    <div style="position: absolute; bottom: 22px; left: 4px; font-size: 16px; color: {LED_RAIL};">
      {#if isLastStop}
        <span style="font-weight: 700; color: #ffffff;">Last Stop</span>
      {:else}
        <span style="font-weight: 700; color: #ffffff;">{stopsAway}</span> {stopsAway === 1 ? 'Stop' : 'Stops'}
      {/if}
    </div>
  </div>
{/snippet}

<!-- The sign band over the full-width dot-matrix strip. Nothing sits beside the strip any
     more, so it simply gets everything the padding leaves — which is what `STRIP_MIN_W` and
     the callout measured against it now mean. -->
<div
  style="
    flex-shrink: 0; display: flex; flex-direction: column; gap: 8px;
    padding: 8px {STRIP_PAD_X}px 0;
  "
>
  <DestinationSign {routeId} {terminus} {notice} {noticeUrgent} trailing={signTrailing} />

  {#if stops.length === 0}
    <div
      style="
        flex: none; height: {STRIP_H}px; display: flex; align-items: center;
        font-size: 20px; {ledGlow(LED_AMBER, 6, 0.45)} font-family: {LED_FONT};
      "
    >
      LOADING STOP DATA…
    </div>
  {:else}
  <!-- Scrolled by dragging it, and by nothing else: the two arrow buttons that used to drive
       it from the sign panel were a desktop affordance on a touch surface that already pans,
       and they cost the panel a row. `touch-action: pan-x` is the whole control.
       ⚠️ `.rollsign-strip` hides the scrollbar, and that is load-bearing, not cosmetic. A
       classic scrollbar is laid OUT — it takes ~15px off the scroller's content height, which
       moves the callout's pivot up with it and pushed the red bar's high corner 12px past the
       top of the strip, where it was sheared. The whole vertical budget in `shell.ts` is
       measured against `ROLLSIGN_STRIP_H`, so anything that quietly reduces it clips the
       callout. (It also drew a grey bar across the amber face.) -->
  <div
    class="rollsign-strip"
    style="
      flex: none; height: {STRIP_H}px;
      overflow-x: auto; overflow-y: hidden; position: relative; z-index: 1;
      touch-action: pan-x;
    "
  >
    <div style="display: flex; height: 100%; width: max-content; padding-right: 40px;">
      <!-- Next stop callout column -->
      {#if nextStop}
        {@const nextTransfers = getStationTransferRoutes(nextStop.stopId)}
        {@const nextIsDest = nextStop.stopId === destinationStopId}
        {@const nextAda = isStationAccessible(nextStop.stopId, nextStop.stopName, nextStop.accessible)}
        {@const nextLabel = `${nextStop.stopName}${nextAda ? ' ♿' : ''}${nextIsDest ? ' 🏁' : ''}`}
        <!-- Same rule as an upcoming stop's column, and this is where it was costing the most:
             the red bar overhangs this column by design and lies over the two columns behind
             it, so an upright hit region meant the rider tapped the frame reading "34 St-Herald
             Sq" and picked 47-50 Sts. The bar itself is the button now. -->
        <div
          style="
            position: relative;
            flex: none; width: {NEXT_COL_W}px; height: 100%;
            font-family: {LED_FONT};
            z-index: {restStops.length + 1};
          "
        >
          <!-- The head of the line. The rail arrives from the right and stops here, turning
               up into a hook — so this column carries no tick: on the sign the terminus IS
               the next stop's mark, and a tick would now run parallel to the red bar a few px
               under its lower edge and read as a second border. -->
          <svg
            width={NEXT_COL_W}
            height={ROLLSIGN_RAIL_SVG_H}
            viewBox="0 0 {NEXT_COL_W} {ROLLSIGN_RAIL_SVG_H}"
            style="position: absolute; left: 0; bottom: {ROLLSIGN_RAIL_BOTTOM}px;"
            aria-hidden="true"
          >
            <path
              d={rollsignRailPath(NEXT_COL_W)}
              fill="none"
              stroke={LED_RAIL}
              stroke-width={ROLLSIGN_RAIL_H}
              stroke-linecap="round"
            />
          </svg>
          <div
            style="
              position: absolute; bottom: {NEXT_BOX_BOTTOM}px; left: {NEXT_BOX_LEFT}px;
              font-size: {NEXT_LABEL_PX}px; line-height: 1;
              {ledGlow(LED_RED, 14, 0.75)}
              transform: {nextLabelTransform}; transform-origin: bottom left;
              letter-spacing: 1.5px; white-space: nowrap;
            "
          >{isArriving ? 'This is' : 'Next Stop'}</div>
          {#key `${nextStop.stopId}-${isArriving}`}
            <button
              onclick={() => onPick(nextStop.stopId)}
              aria-label={nextStop.stopId === destinationStopId
                ? `Next stop ${nextStop.stopName}, don't get off here`
                : `Next stop ${nextStop.stopName}, get off here`}
              aria-pressed={nextStop.stopId === destinationStopId}
              class={isArriving ? 'blink-red-box' : ''}
              style="
                position: absolute; bottom: {NEXT_BOX_BOTTOM}px; left: {NEXT_BOX_LEFT}px;
                width: {NEXT_BOX_W}px; height: {NEXT_BOX_H}px; box-sizing: border-box;
                border: {NEXT_BOX_BORDER}px solid {LED_RED}; border-radius: 2px;
                box-shadow: 0 0 14px rgba(255,59,48,.55);
                padding: 0 {NEXT_BOX_PAD_X}px; text-align: left;
                transform: {nextBarTransform}; transform-origin: bottom left; white-space: nowrap;
                display: flex; flex-direction: column; align-items: flex-start;
                justify-content: center;
                background: none; cursor: pointer; font: inherit;
              "
            >
              <div style="font-size: {fitName(nextLabel, NEXT_BOX_INNER_W)}px; line-height: 1.2; letter-spacing: 1px; width: 100%; overflow: hidden; text-overflow: ellipsis; {ledGlow(nextIsDest ? LED_GREEN : LED_AMBER, 8)}">
                <span>{nextStop.stopName}</span>{#if nextAda}<span style="font-size: 0.625em; line-height: 1; vertical-align: middle; margin: 0 1px 0 2px;">♿</span>{/if}{#if nextIsDest} 🏁{/if}
              </div>
              {#if nextTransfers.length > 0}
                <div style="font-size: {ROLLSIGN_TRANSFER_PX}px; letter-spacing: 1px; width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; {ledGlow(LED_GREEN, 6, 0.55)}; margin-top: 2px;">
                  {nextTransfers.join('·')}
                </div>
              {/if}
            </button>
          {/key}
        </div>
      {/if}

      {#each restStops as stop, i (stop.stopId)}
        {@render stopColumn(
          stop.stopId,
          stop.stopName,
          i + 2,
          COL_W,
          stop.stopId === destinationStopId,
          i === restStops.length - 1,
          stop.accessible,
          restStops.length - i,
        )}
      {/each}
    </div>
  </div>
  {/if}
</div>

<style>
  /* See the strip's own comment: a laid-out scrollbar steals height the callout's geometry
     is measured against. Overlay scrollbars (Android's WebView, mobile Chrome) cost nothing,
     but a desktop browser during dev has classic ones and clipped the bar. */
  .rollsign-strip {
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  .rollsign-strip::-webkit-scrollbar {
    display: none;
  }

  @keyframes blinkRedBox {
    0%, 100% {
      opacity: 1;
      border-color: #ff3b30;
      box-shadow: 0 0 14px rgba(255, 59, 48, 0.55);
    }
    50% {
      opacity: 0.2;
      border-color: rgba(255, 59, 48, 0.2);
      box-shadow: 0 0 2px rgba(255, 59, 48, 0.1);
    }
  }

  .blink-red-box {
    animation: blinkRedBox 1.6s ease-in-out infinite;
  }
</style>

